import request, { ResponseError } from '@capturi/request'
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  CopilotConfig,
  CopilotCreateOrUpdateConfig,
  TokenPayload,
  TokenResponse,
} from './types'

export const useCreateCopilotConfig = (
  organizationUid: string,
): UseMutationResult<
  CopilotCreateOrUpdateConfig,
  ResponseError,
  CopilotCreateOrUpdateConfig,
  ResponseError
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (model: CopilotCreateOrUpdateConfig) => {
      return request.post<CopilotConfig>(
        `co-pilot/v1/organizations/${organizationUid}`,
        {
          json: model,
        },
      )
    },
    onSuccess: (config) => {
      queryClient.setQueryData<CopilotConfig>(
        ['CoPilotConfig', organizationUid],
        (oldData) => {
          if (!oldData) {
            return config
          }
          return { ...oldData, ...config }
        },
      )
    },
  })
}

export const useGetCopilotConfig = (
  organizationUid: string,
): UseQueryResult<CopilotConfig> => {
  return useQuery({
    queryKey: ['CoPilotConfig', organizationUid],
    queryFn: () =>
      request.get<CopilotConfig>(
        `co-pilot/v1/organizations/${organizationUid}`,
      ),
  })
}

export const useUpdateCopilotConfig = (
  organizationUid: string,
): UseMutationResult<
  Partial<CopilotConfig>,
  ResponseError,
  Partial<CopilotConfig>,
  ResponseError
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (config: Partial<CopilotCreateOrUpdateConfig>) =>
      request.patch<CopilotConfig>(
        `co-pilot/v1/organizations/${organizationUid}`,
        {
          json: config,
        },
      ),
    onSuccess: (config) => {
      queryClient.setQueryData<CopilotConfig>(
        ['CoPilotConfig', organizationUid],
        (oldData) => {
          if (!oldData) {
            return config
          }
          return { ...oldData, ...config }
        },
      )
    },
  })
}

export const useCreateToken = (
  organizationUid: string,
): UseMutationResult<
  TokenResponse,
  ResponseError,
  TokenPayload,
  ResponseError
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tokenPayload: TokenPayload) =>
      request.post<TokenResponse>(
        `co-pilot/v1/organizations/${organizationUid}/tokens`,
        {
          json: tokenPayload,
        },
      ),
    onSuccess: (newToken) => {
      queryClient.setQueryData<CopilotConfig>(
        ['CoPilotConfig', organizationUid],
        (oldData) => {
          if (!oldData) {
            return
          }

          return {
            ...oldData,
            tokens: [...oldData.tokens, newToken],
          }
        },
      )
    },
  })
}

export const useDeleteToken = (
  organizationUid: string,
): UseMutationResult<TokenResponse, ResponseError, string, ResponseError> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tokenUid: string) =>
      request.delete<TokenResponse>(
        `co-pilot/v1/organizations/${organizationUid}/tokens/${tokenUid}`,
      ),
    onSuccess: (_, tokenUid) => {
      queryClient.setQueryData<CopilotConfig>(
        ['CoPilotConfig', organizationUid],
        (oldData) => {
          if (!oldData) {
            return
          }
          return {
            ...oldData,
            tokens: oldData.tokens.filter((token) => token.uid !== tokenUid),
          }
        },
      )
    },
  })
}
